import React, { useCallback, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Card, CardBody } from "reactstrap";
import {
  customerBadCookingSessionsColumns,
  customerCookingSessionsColumns,
  customerAssetsColumns,
  TABS,
  tasksHistoryColumns,
  getTabsButtons,
  // COOKING_SESSION_TYPE,
} from "pages/customer-management/customerDetails/constants";
import classnames from "classnames";
import PersonalInformationTab from "pages/customer-management/customerDetails/Tabs/PersonalInformationTab";
import TransactionTab from "pages/customer-management/customerDetails/Tabs/TransactionTab";
import CookingSessionsTab from "pages/customer-management/customerDetails/Tabs/CookingSessionsTab";
import NGITable from "components/Common/NGITable";
import BalanceBookTab from "pages/customer-management/customerDetails/Tabs/BalanceBookTab";
import { useLocation } from "react-router";
import CustomButton from "components/Common/buttons/CustomButton";
import { customDateFormatWithTime } from "components/Common/formatter";
import { generateXLS, userHasAdminRole, userHasOneOfRoles } from "utils/helpers/functions";
import DemographicQuestionsTab from "pages/operations/leadDetails/Tabs/DemographicQuestionsTab";
import CreditBookTab from "pages/customer-management/customerDetails/Tabs/CreditBookTab";
import ContractHistoryTab from "pages/customer-management/customerDetails/Tabs/ContractHistoryTab";
import LinkedAccountsTab from "pages/customer-management/customerDetails/Tabs/LinkedAccountsTab";
import UnitPriceTab from "pages/customer-management/customerDetails/Tabs/UnitPriceTab";
import BundlesTab from "pages/customer-management/customerDetails/Tabs/BundlesTab";

const useStyles = makeStyles({
  root: {
    width: "100%",
    marginTop: 30,
    display: "flex",
  },
  container: {
    borderRadius: 30,
    width: "100%",
  },
  tabs: {
    display: "flex",
    padding: "30px 0",
    fontSize: 13,
    fontWeight: 600,
    flexDirection: "column",
    marginRight: -4,
  },
  tab: {
    backgroundColor: "#E1E1E1",
    padding: 15,
    borderRadius: "10px 0 0 10px",
    marginBottom: 10,
    whiteSpace: "nowrap",

    "&:hover": {
      cursor: "pointer",
    },
  },
  active: {
    backgroundColor: "#FFFFFF!important",
  },
  dateTime: {
    marginBottom: 10,
  },
  typeSelect: {
    width: 190,
    borderRadius: 30,
    border: "1px solid #00000029",
    marginLeft: 13,
  },
  overflowText: {
    overflow: "hidden",
    width: 150,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
});

const DataTabs = ({
  customer,
  cookingSessions,
  badCookingSessions,
  assets,
  customerBalance,
  tasksHistory,
  contractUrl,
  balanceBookTransactions,
  updateAssets,
  updateHistory,
  handleCookingSessions,
  handleBadCookingSessions,
  updateBalanceBookTransactions,
  isLoading,
  consumptionStat,
  lastCylinderChange,
  photoDetails,
  questionnaire,
  creditBook,
  creditBookTransactions,
  handleCreditBookTransactions,
  meterPinDetails,
  isBlacklisted,
  setIsBlacklisted,
}) => {
  const classes = useStyles();
  const searchParams = useLocation().search;
  const activeUrlTab = new URLSearchParams(searchParams).get("activeTab");
  const [activeTab, setActiveTab] = useState(activeUrlTab ?? TABS.PERSONAL_INFORMATION);
  // const [sessionType, setSessionType] = useState(COOKING_SESSION_TYPE[0].type);

  const onTabClick = useCallback(e => {
    const tab = e.target.dataset.tab;
    setActiveTab(tab);
  }, []);

  const normalizedAssets = useMemo(() => {
    const newAssets = { ...assets };
    if (!!assets?.result?.items && customer?.status === "Suspended")
      newAssets.result.items = assets?.result?.items?.filter(item => ["Missing", "Lost"].includes(item.state));
    return newAssets;
  }, [assets, customer]);

  const onDownload = useCallback(
    ({ isGood = true, name, reportsList }) =>
      generateXLS(
        reportsList,
        `${name}, ${isGood ? "Good" : "Bad"} cooking sessions, ${customDateFormatWithTime(Date.now())}`,
        "xlsx"
      ),
    []
  );

  // const handleTypeChange = useCallback(type => {
  //   setSessionType(type);
  //   handleCookingSessions({ cookingSessionType: type });
  // }, []);

  const handleCookingSessionsChange = useCallback(filters => {
    handleCookingSessions({ ...filters });
  }, []);

  const isHavePXMeter = useMemo(() => !!assets?.result?.items?.find(item => item.type.includes("PX")), [assets]);

  return (
    <div className={classes.root}>
      <div className={classes.tabs}>
        {getTabsButtons(customer?.status)?.map(item => {
          if (!userHasAdminRole() && userHasOneOfRoles(item?.hideRoles)) return null;
          return (
            <div
              key={item.key}
              className={classnames(classes.tab, activeTab === item.key && classes.active)}
              data-tab={item.key}
              onClick={onTabClick}
            >
              {item.title}
            </div>
          );
        })}
      </div>
      <Card className={classes.container}>
        <CardBody>
          {activeTab === TABS.PERSONAL_INFORMATION && (
            <PersonalInformationTab
              customer={customer}
              contract={contractUrl}
              consumptionStat={consumptionStat}
              lastCylinderChange={lastCylinderChange}
              photoDetails={photoDetails}
              meterPinDetails={meterPinDetails}
              isHavePXMeter={isHavePXMeter}
            />
          )}
          {activeTab === TABS.DEMOGRAPHIC_QUESTIONS && (
            <DemographicQuestionsTab
              mode={""}
              editForm={null}
              registrationSurvey={customer?.registrationSurvey}
              leadQuestionnaire={questionnaire}
            />
          )}
          {activeTab === TABS.TRANSACTION_BOOK && <TransactionTab customerId={customer.id} />}
          {activeTab === TABS.BALANCE_BOOK && (
            <BalanceBookTab
              isLoading={isLoading}
              customerBalance={customerBalance}
              update={updateBalanceBookTransactions}
              transactions={balanceBookTransactions}
            />
          )}
          {activeTab === TABS.CREDIT_BOOK && (
            <CreditBookTab
              isLoading={isLoading}
              creditBook={creditBook}
              update={handleCreditBookTransactions}
              transactions={creditBookTransactions}
            />
          )}
          {activeTab === TABS.COOKING_SESSIONS && (
            <>
              <div className="filters-wrapper">
                <div className="filter-buttons">
                  <CustomButton
                    icon={<i className="bx bx-download" />}
                    type="primary"
                    size="small"
                    disabled={!cookingSessions?.result?.items?.length}
                    text="Download"
                    onClick={() => {
                      onDownload({
                        isGood: true,
                        reportsList: cookingSessions.result?.items,
                        name: `${customer?.firstName} ${customer?.lastName}`,
                      });
                    }}
                  />
                </div>
                {/*<div>*/}
                {/*  Cooking Sessions Type*/}
                {/*  <NGISelect*/}
                {/*    size="small"*/}
                {/*    className={classes.typeSelect}*/}
                {/*    value={sessionType}*/}
                {/*    onChange={handleTypeChange}*/}
                {/*  >*/}
                {/*    {COOKING_SESSION_TYPE.map(item => (*/}
                {/*      <Option value={item.type} key={item.type}>*/}
                {/*        {item.title}*/}
                {/*      </Option>*/}
                {/*    ))}*/}
                {/*  </NGISelect>*/}
                {/*</div>*/}
              </div>
              <CookingSessionsTab
                isLoading={isLoading}
                update={handleCookingSessionsChange}
                columns={customerCookingSessionsColumns()}
                list={cookingSessions}
              />
            </>
          )}
          {activeTab === TABS.BAD_COOKING_SESSIONS && (
            <>
              <div className="filters-wrapper">
                <CustomButton
                  icon={<i className="bx bx-download" />}
                  type="primary"
                  size="small"
                  disabled={!badCookingSessions?.result?.items?.length}
                  text="Download"
                  onClick={() => {
                    onDownload({
                      isGood: false,
                      reportsList: badCookingSessions.result?.items,
                      name: `${customer?.firstName} ${customer?.lastName}`,
                    });
                  }}
                />
              </div>
              <CookingSessionsTab
                isLoading={isLoading}
                update={handleBadCookingSessions}
                columns={customerBadCookingSessionsColumns()}
                list={badCookingSessions}
              />
            </>
          )}
          {activeTab === TABS.ASSETS && (
            <NGITable
              isLoading={isLoading}
              updateList={updateAssets}
              columns={customerAssetsColumns()}
              data={normalizedAssets}
            />
          )}
          {activeTab === TABS.TASKS_HISTORY && (
            <>
              {customer.lastInterventionAt && (
                <div className={classes.dateTime}>
                  Last Intervention Date: <b>{customDateFormatWithTime(customer.lastInterventionAt)}</b>
                </div>
              )}
              <NGITable
                isLoading={isLoading}
                updateList={updateHistory}
                columns={tasksHistoryColumns(customer.id, classes)}
                data={tasksHistory}
              />
            </>
          )}
          {activeTab === TABS.CONTRACT_HISTORY && (
            <ContractHistoryTab customer={customer} contracts={customer.contracts} />
          )}
          {activeTab === TABS.UNIT_PRICE && <UnitPriceTab customer={customer} />}
          {activeTab === TABS.LINKED_ACCOUNTS && <LinkedAccountsTab customer={customer} />}
          {activeTab === TABS.BUNDLES && (
            <BundlesTab
              customer={customer}
              consumptionStat={consumptionStat}
              isBlacklisted={isBlacklisted}
              setIsBlacklisted={setIsBlacklisted}
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default DataTabs;
